import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { AppShell } from "@/components/TopNav";
import { PageHeader, Kpi } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ValueVsContributions, DonutAllocation, CHART_COLORS } from "@/components/Charts";
import { PerformersPanel } from "@/components/insights/PerformersPanel";
import { toast } from "sonner";
import { fmtUSD } from "@/lib/format";
import { LineChart as LineIcon } from "lucide-react";

const RANGES = ["1M", "3M", "1Y", "ALL"];

export default function Insights() {
  const [range, setRange] = useState("3M");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await api.get(`/insights/portfolio?range=${range}`); setData(d); }
    catch (e) { toast.error("Could not load your insights"); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const alloc = (data?.allocation || []).map((a) => ({ name: a.name, value: a.value }));

  return (
    <AppShell>
      <PageHeader title="Insights" subtitle="How your money has actually performed — value, contributions and movers"
        testid="insights-header"
        action={
          <Tabs value={range} onValueChange={setRange}>
            <TabsList data-testid="insights-range-tabs">
              {RANGES.map((r) => <TabsTrigger key={r} value={r} data-testid={`insights-range-${r}`}>{r}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        } />

      <div className="grid gap-4 sm:gap-5 grid-cols-2 lg:grid-cols-4 mb-4 sm:mb-5">
        <Kpi label="Net worth" value={fmtUSD(data?.net_worth_usd || 0)} testid="insight-net-worth" accent />
        <Kpi label="Net deposits" value={fmtUSD(data?.total_contributed_usd || 0)} testid="insight-contributed" />
        <Kpi label="Holdings P&L" value={fmtUSD(data?.holdings_pnl_usd || 0)} change={data?.holdings_pnl_pct}
          testid="insight-holdings-pnl" />
        <Kpi label={`Change (${range})`} value={fmtUSD(data?.period_change_usd || 0)} change={data?.period_change_pct}
          testid="insight-period-change" />
      </div>

      <div className="grid gap-4 sm:gap-5 lg:grid-cols-3 mb-4 sm:mb-5">
        <Card className="p-5 shadow-premium lg:col-span-2" data-testid="insights-chart-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-medium"><LineIcon className="h-4 w-4" />Value vs. money in</div>
            <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-[#A7C63F]" />Portfolio value</span>
              <span className="flex items-center gap-1.5"><span className="h-0.5 w-4 bg-[#C4DC7C]" />Net deposits</span>
            </div>
          </div>
          {loading || !data?.series?.length ? (
            <div className="h-[300px] flex items-center justify-center text-sm text-muted-foreground">Loading…</div>
          ) : (
            <ValueVsContributions data={data.series} height={300} />
          )}
        </Card>

        <Card className="p-5 shadow-premium" data-testid="insights-allocation-card">
          <div className="text-sm font-medium mb-3">Where your money sits</div>
          {alloc.length ? (
            <>
              <DonutAllocation data={alloc} height={190} />
              <div className="mt-3 space-y-1.5">
                {alloc.map((a, i) => (
                  <div key={a.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 capitalize">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {a.name}
                    </span>
                    <span className="numeric text-xs">{fmtUSD(a.value)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-muted-foreground">Nothing allocated yet.</p>}
        </Card>
      </div>

      <PerformersPanel best={data?.best || []} worst={data?.worst || []} />
    </AppShell>
  );
}
